import type { StepExecutionItem } from "../../shared/executions/executionListTypes";

type NavCandidate = Pick<StepExecutionItem, "id" | "status">;

export type ExecutionNavTargets<T> = {
  older: T | undefined;
  newer: T | undefined;
  olderSuccess: T | undefined;
  newerSuccess: T | undefined;
};

/**
 * Resolves the adjacent run and nearest successful run the nav buttons jump to.
 * `executions` is latest-first, so a lower index is newer and a higher index is
 * older. Only the loaded window is searched; everything is empty when the
 * selection isn't in the list. Failures are resolved separately against
 * execution_failures so they can reach beyond the window.
 */
export function executionNavTargets<T extends NavCandidate>(
  executions: readonly T[],
  selectedId: string
): ExecutionNavTargets<T> {
  const idx = executions.findIndex((e) => e.id === selectedId);
  if (idx === -1) {
    return {
      older: undefined,
      newer: undefined,
      olderSuccess: undefined,
      newerSuccess: undefined,
    };
  }
  const at = (i: number) =>
    i >= 0 && i < executions.length ? executions[i] : undefined;
  const searchSuccess = (step: 1 | -1) => {
    for (let i = idx + step; i >= 0 && i < executions.length; i += step) {
      const candidate = executions[i];
      if (candidate && candidate.status === "success") return candidate;
    }
    return undefined;
  };
  return {
    newer: at(idx - 1),
    older: at(idx + 1),
    newerSuccess: searchSuccess(-1),
    olderSuccess: searchSuccess(1),
  };
}
